import { AutoAwesome } from "@mui/icons-material";
import { LoadingButton, LoadingButtonProps } from "@mui/lab";
import { Tooltip } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { TCopilotNotePopulationStatus } from "../../../../../../../types/copilot";
import { useCopilotClinicalContext } from "../../useCopilotClinicalContext";
import { useCopilotSectionApply } from "../../hooks/useCopilotSectionApply";
import {
  CopilotHumanEditedSection,
  shouldShowSectionAcceptButton,
} from "../utils/copilotExtensions";

type CopilotSectionAcceptButtonProps = {
  sections: CopilotHumanEditedSection[];
  populationStatusField?: keyof TCopilotNotePopulationStatus;
  onAccept: () => Promise<unknown>;
  defaultTooltip?: string;
  label?: string;
  loadingLabel?: string;
} & Pick<
  LoadingButtonProps,
  "size" | "variant" | "sx" | "fullWidth" | "disabled"
>;

const CopilotSectionAcceptButton = ({
  sections,
  populationStatusField,
  onAccept,
  defaultTooltip = "Add content from the note to the chart",
  label = "Accept",
  loadingLabel = "Accepting...",
  size = "small",
  variant = "outlined",
  sx,
  fullWidth,
  disabled,
}: CopilotSectionAcceptButtonProps) => {
  const [loading, setLoading] = useState(false);
  const { enableAiNoteAcceptance } = useFlags();
  const { copilotData } = useCopilotClinicalContext();
  const { applySection, getAcceptTooltip, getPopulationAcceptTooltip } =
    useCopilotSectionApply();

  if (
    !enableAiNoteAcceptance ||
    !shouldShowSectionAcceptButton(copilotData, {
      populationStatusField,
      sections,
    })
  ) {
    return null;
  }

  const tooltip = populationStatusField
    ? getPopulationAcceptTooltip(populationStatusField, defaultTooltip)
    : sections.reduce(
        (message, section) =>
          message === defaultTooltip
            ? getAcceptTooltip(section, defaultTooltip)
            : message,
        defaultTooltip,
      );

  const handleClick = async () => {
    setLoading(true);
    try {
      await applySection({
        sections,
        populationStatusField,
        populate: onAccept,
      });
    } catch {
      // Populate handlers surface their own errors; preserve edit flags.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title={tooltip}>
      <span>
        <LoadingButton
          startIcon={<AutoAwesome />}
          size={size}
          variant={variant}
          sx={sx}
          fullWidth={fullWidth}
          loading={loading}
          disabled={disabled || loading}
          onClick={handleClick}
        >
          {loading ? loadingLabel : label}
        </LoadingButton>
      </span>
    </Tooltip>
  );
};

export default CopilotSectionAcceptButton;
